import React, { createContext, useContext, useMemo, FC, PropsWithChildren } from 'react';
import { fade, makeStyles, Theme } from '@material-ui/core/styles';
import {
  buildDiffMap,
  DiffMap,
  DiffType,
  groupDiff,
  IdMatchedPaths,
  isIdMatched,
  itemKey,
  joinItemPath,
  joinPath,
  pathDiff,
  idMatchedPaths,
} from '~/utils/diff';
import { useDraft } from '~/hooks/useDraftContext';
import { useEditMode } from '~/hooks/useEditModeContext';

interface DraftDiffValue {
  map: DiffMap;
  /** The active version the draft is compared against. */
  original: any;
  /** The record the map describes, to tell it from records shown alongside it. */
  current: any;
  /** Kept here so DiffScope keys elements the way the map keyed them. */
  idMatched: IdMatchedPaths;
}

const EMPTY: DraftDiffValue = {
  map: {},
  original: undefined,
  current: undefined,
  idMatched: [],
};

const DraftDiffContext = createContext<DraftDiffValue>(EMPTY);

const DiffScopeContext = createContext<string | undefined>(undefined);

interface ProviderProps {
  /** The record on screen. Everything else is taken from the draft context. */
  draft?: any;
}

/**
 * Active only in DRAFT mode and only once the active version has loaded, so
 * outside a draft every lookup below reports "unchanged" and nothing is tinted.
 */
export const DraftDiffProvider: FC<PropsWithChildren<ProviderProps>> = ({ draft, children }) => {
  const { editMode } = useEditMode();
  const { activeRecord, page } = useDraft();
  const idMatched = idMatchedPaths(page?.resource);

  const value = useMemo<DraftDiffValue>(() => {
    if (editMode !== 'DRAFT' || !activeRecord || !draft) return EMPTY;
    return {
      map: buildDiffMap(activeRecord, draft, idMatched),
      original: activeRecord,
      current: draft,
      idMatched,
    };
  }, [editMode, activeRecord, draft, idMatched]);

  return <DraftDiffContext.Provider value={value}>{children}</DraftDiffContext.Provider>;
};

interface ScopeProps {
  source: string;
  index?: number;
  /** The element itself — needed only for collections matched by id. */
  item?: any;
}

/**
 * Lets the fields inside stay unaware of where they sit: `<DiffScope
 * source='documents' index={i}>` makes a nested `source='number'` resolve to
 * `documents[i].number`.
 */
export const DiffScope: FC<PropsWithChildren<ScopeProps>> = ({ source, index, item, children }) => {
  const parent = useContext(DiffScopeContext);
  const { idMatched } = useContext(DraftDiffContext);

  const base = joinPath(parent, source);
  const path =
    index === undefined
      ? base
      : joinItemPath(base, itemKey(item, index, isIdMatched(base, idMatched)));

  return <DiffScopeContext.Provider value={path}>{children}</DiffScopeContext.Provider>;
};

export const useDiffPath = (source?: string): string | undefined => {
  const scope = useContext(DiffScopeContext);
  if (!source) return scope;
  return joinPath(scope, source);
};

/**
 * Pass `record` where the field may be showing something other than the record
 * being compared — a ReferenceField swaps in the entity it resolved, whose field
 * names would otherwise be looked up against the wrong object.
 */
export const useFieldDiff = (source?: string, record?: any): DiffType | undefined => {
  const { map, current } = useContext(DraftDiffContext);
  const path = useDiffPath(source);

  if (record !== undefined && record !== current) return undefined;
  return path ? pathDiff(map, path) : undefined;
};

export const useGroupDiff = (source?: string): DiffType | undefined => {
  const { map } = useContext(DraftDiffContext);
  const path = useDiffPath(source);
  return useMemo(() => (path ? groupDiff(map, path) : undefined), [map, path]);
};

export const useDraftOriginal = (): any => useContext(DraftDiffContext).original;

/** Scope-bound lookups, for components rendering a variable number of fields. */
export const useDiffLookup = () => {
  const { map, idMatched } = useContext(DraftDiffContext);
  const scope = useContext(DiffScopeContext);

  return useMemo(
    () => ({
      field: (source?: string) => pathDiff(map, joinPath(scope, source ?? '')),
      group: (source?: string) => groupDiff(map, joinPath(scope, source ?? '')),
      /** Element path keyed the way the diff map keyed it — `files[2]` or `files[<id>]`. */
      itemPath: (source: string, item: any, index: number) => {
        const byId = isIdMatched(joinPath(scope, source), idMatched);
        return joinItemPath(source, itemKey(item, index, byId));
      },
    }),
    [map, scope, idMatched]
  );
};

/**
 * Same three colours as the approval case screen, so a change reads the same way
 * on both. Labels stay neutral, otherwise a coloured caption competes with the
 * value it describes.
 */
const useValueStyles = makeStyles((theme: Theme) => ({
  changed: {
    color: theme.palette.warning.main,
    '& label': { color: theme.palette.text.primary },
  },
  added: {
    color: theme.palette.success.main,
    '& label': { color: theme.palette.text.primary },
  },
  removed: {
    color: theme.palette.error.main,
    '& label': { color: theme.palette.text.primary },
  },
  unchanged: {},
}));

export const useDiffValueClass = (diff?: DiffType): string | undefined => {
  const classes = useValueStyles();
  return diff && diff !== 'unchanged' ? classes[diff] : undefined;
};

export const useDiffClassFor = (): ((source?: string) => string | undefined) => {
  const classes = useValueStyles();
  const lookup = useDiffLookup();

  return (source?: string) => {
    const diff = lookup.field(source);
    return diff && diff !== 'unchanged' ? classes[diff] : undefined;
  };
};

/** For values that always exist, and so can never be new — see the row variant below. */
export const useAnyDiffValueClass = (diff?: DiffType): string | undefined => {
  const classes = useValueStyles();
  return diff && diff !== 'unchanged' ? classes.changed : undefined;
};

export const useAnyDiffClassFor = (): ((source?: string) => string | undefined) => {
  const classes = useValueStyles();
  const lookup = useDiffLookup();

  return (source?: string) => {
    const diff = lookup.field(source);
    return diff && diff !== 'unchanged' ? classes.changed : undefined;
  };
};

/** For images, which cannot be tinted. A removed one is not on screen to outline. */
const useBorderStyles = makeStyles((theme: Theme) => ({
  changed: { border: `2px solid ${fade(theme.palette.warning.main, 0.5)}` },
  added: { border: `2px solid ${fade(theme.palette.success.main, 0.5)}` },
  removed: {},
  unchanged: {},
}));

export const useDiffBorderClass = (diff?: DiffType): string | undefined => {
  const classes = useBorderStyles();
  return diff === 'added' || diff === 'changed' ? classes[diff] : undefined;
};

export const useDiffBorderClassFor = (): ((source?: string) => string | undefined) => {
  const classes = useBorderStyles();
  const lookup = useDiffLookup();

  return (source?: string) => {
    const diff = lookup.group(source);
    return diff === 'added' || diff === 'changed' ? classes[diff] : undefined;
  };
};

/**
 * For blocks that read as one unit — a document card, an address row. Green when
 * the entry is wholly new, red when it is wholly gone; a part added, edited or
 * dropped inside an existing one is amber, since all three mean the same from
 * the outside: it is not what it was.
 *
 * `outline` rather than `border`, so the frame costs no layout; a row is framed
 * through its cells, which is what survives border-collapse.
 */
const useOutlineStyles = makeStyles((theme: Theme) => {
  const block = (main: string) => {
    const color = fade(main, 0.5);
    return { outline: `2px solid ${color}`, outlineOffset: -2 };
  };
  const row = (main: string) => {
    const color = fade(main, 0.5);
    return {
      '& > td': { borderTop: `2px solid ${color}`, borderBottom: `2px solid ${color}` },
      '& > td:first-child': { borderLeft: `2px solid ${color}` },
      '& > td:last-child': { borderRight: `2px solid ${color}` },
    };
  };

  return {
    blockAdded: block(theme.palette.success.main),
    blockChanged: block(theme.palette.warning.main),
    blockRemoved: block(theme.palette.error.main),
    rowAdded: row(theme.palette.success.main),
    rowChanged: row(theme.palette.warning.main),
    rowRemoved: row(theme.palette.error.main),
  };
});

type OutlineClasses = ReturnType<typeof useOutlineStyles>;

const blockOutline = (classes: OutlineClasses, diff?: DiffType): string | undefined => {
  if (!diff || diff === 'unchanged') return undefined;
  if (diff === 'added') return classes.blockAdded;
  if (diff === 'removed') return classes.blockRemoved;
  return classes.blockChanged;
};

const rowOutline = (classes: OutlineClasses, diff?: DiffType): string | undefined => {
  if (!diff || diff === 'unchanged') return undefined;
  if (diff === 'added') return classes.rowAdded;
  if (diff === 'removed') return classes.rowRemoved;
  return classes.rowChanged;
};

export const useDiffOutlineClass = (diff?: DiffType): string | undefined =>
  blockOutline(useOutlineStyles(), diff);

export const useDiffRowOutlineClass = (diff?: DiffType): string | undefined =>
  rowOutline(useOutlineStyles(), diff);

export const useDiffOutlineClassFor = (): ((source?: string) => string | undefined) => {
  const classes = useOutlineStyles();
  const lookup = useDiffLookup();

  return (source?: string) => blockOutline(classes, lookup.group(source));
};

export const useDiffRowOutlineClassFor = (): ((source?: string) => string | undefined) => {
  const classes = useOutlineStyles();
  const lookup = useDiffLookup();

  return (source?: string) => rowOutline(classes, lookup.group(source));
};

/** A limit left unset still has the schema default, so its row is never new. */
export const useAnyDiffRowOutlineClassFor = (): ((source?: string) => string | undefined) => {
  const classes = useOutlineStyles();
  const lookup = useDiffLookup();

  return (source?: string) => {
    const diff = lookup.group(source);
    return diff && diff !== 'unchanged' ? classes.rowChanged : undefined;
  };
};
